import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';

import { getRiders } from '../../../../api/services/Riders';

type Option = {
  value: string;
  label: string;
};

interface UseRiders {
  riders: Option[];
  ridersLoading: boolean;
  getRiderOptions: () => void;
  deboucedQueryRiders: (query: string) => void;
}

type Query = string | undefined;
export const useRiders = (): UseRiders => {
  const [initialRiders, setInitialRiders] = useState<Nullable<Option[]>>(null);
  const [riders, setRiders] = useState<Option[]>([]);
  const [ridersLoading, setRidersLoading] = useState<boolean>(false);
  let abortController = new AbortController();
  const [hasCachedInitialRequest, setHasCachedInitialRequest] = useState(false);

  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, []);

  const getRiderOptions = useCallback(
    async (query?: Query) => {
      if (!query && initialRiders) {
        setRiders(initialRiders);

        return;
      }

      initialRiders && abortController.abort();
      await fetchRiders(query);
    },
    [initialRiders]
  );

  const fetchRiders = useCallback(
    async (query?: Query) => {
      abortController = new AbortController();
      setRidersLoading(true);
      const response = await getRiders({ q: query }, abortController.signal);

      if (abortController.signal.aborted) {
        return;
      }
      if (response?.type === 'success') {
        const ridersOptions = response.data.riders.map((r) => ({ value: r.slug, label: r.fullName }));

        setRiders(ridersOptions);
        if (!hasCachedInitialRequest && !query) {
          setHasCachedInitialRequest(true);
          setInitialRiders(ridersOptions);
        }
      }
      setRidersLoading(false);
    },
    [hasCachedInitialRequest]
  );

  const deboucedQueryRiders = debounce(getRiderOptions, 400);

  return { riders, ridersLoading, getRiderOptions, deboucedQueryRiders };
};
